import { ItemType } from '../types';

/**
 * Extracts the file extension from a filename or URL.
 * @param filename - The filename or URL to extract the extension from
 * @returns The lowercase file extension without the dot (e.g., "pdf", "docx")
 */
export function getFileExtension(filename: string): string {
  if (!filename) return '';
  
  // Remove query parameters and fragments
  const cleanFilename = filename.split('?')[0].split('#')[0];
  
  // Extract extension
  const match = cleanFilename.match(/\.([^.]+)$/);
  return match ? match[1].toLowerCase() : '';
}

/**
 * Maps a file extension to an ItemType category.
 * @param extension - The file extension (without dot)
 * @returns The corresponding ItemType
 */
export function getFileTypeFromExtension(extension: string): ItemType {
  const ext = extension.toLowerCase();

  // PDF documents
  if (ext === 'pdf') return 'pdf';

  // Word / word-processing documents
  if (['doc', 'docx', 'docm', 'dot', 'dotx', 'odt'].includes(ext)) return 'doc';

  // Excel / spreadsheets
  if (['xls', 'xlsx', 'xlsm', 'xlsb', 'csv', 'ods'].includes(ext)) return 'excel';

  // PowerPoint / presentations
  if (['ppt', 'pptx', 'pptm', 'pps', 'ppsx', 'odp'].includes(ext)) return 'powerpoint';

  // Plain-text documents
  if (['txt', 'rtf', 'md'].includes(ext)) return 'text';

  // Archives
  if (['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'bz2'].includes(ext)) return 'archive';

  // Images
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tif', 'tiff', 'ico', 'heic', 'heif'].includes(ext))
    return 'image';

  // Video
  if (['mp4', 'mov', 'avi', 'webm', 'mkv', 'wmv', 'flv', 'm4v', 'mpg', 'mpeg', '3gp'].includes(ext)) return 'video';

  // Audio
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'wma', 'aiff'].includes(ext)) return 'audio';

  // Default fallback for unrecognised extensions
  return 'guide';
}

/**
 * Gets the ItemType for a given filename by extracting and mapping its extension.
 * @param filename - The filename or URL to analyze
 * @returns The corresponding ItemType
 */
export function getFileType(filename: string): ItemType {
  const extension = getFileExtension(filename);
  return getFileTypeFromExtension(extension);
}

/**
 * Gets a human-readable label for an ItemType.
 * This should be used when you already have the type determined, rather than re-parsing URLs.
 * @param type - The ItemType to get a label for
 * @returns A display label (e.g., "PDF", "Guide", "DOCX")
 */
export function getItemTypeLabel(type: ItemType): string {
  const labels: Record<ItemType, string> = {
    'guide': 'Guide',
    'pdf': 'PDF',
    'tool': 'Tool',
    'doc': 'DOCX',
    'excel': 'XLSX',
    'powerpoint': 'PPTX',
    'text': 'TXT',
    'archive': 'ZIP',
    'image': 'Image',
    'video': 'Video',
    'audio': 'Audio',
  };
  
  return labels[type] || 'File';
}
